/**
 * Fase 3 · Acciones sociales en el perfil público del centro.
 * - Botón Seguir / Siguiendo (toggle, requiere login customer)
 * - Botón Mensaje (abre modal con form)
 * - Contador de seguidores
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { customerApi, useCustomerAuth } from "../../lib/customerAuth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

export default function SocialActions({ slug, orgName }) {
  const navigate = useNavigate();
  const { account } = useCustomerAuth();
  const [stats, setStats] = useState({ followers_count: 0, is_following: false });
  const [busy, setBusy] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    customerApi.get(`/public/orgs/${slug}/social-stats`).then(r => setStats(r.data)).catch(() => {});
  }, [slug]);

  const toggleFollow = async () => {
    if (!account) {
      toast.info("Inicia sesión para seguir a este centro");
      navigate(`/cliente/login?return=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await customerApi.post(`/public/orgs/${slug}/follow`);
      setStats((s) => ({ ...s, ...data }));
      toast.success(data.following ? "Siguiendo este centro" : "Has dejado de seguir");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo procesar");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="centro-social-actions">
        <Button
          onClick={toggleFollow}
          disabled={busy}
          size="sm"
          variant={stats.is_following ? "outline" : "default"}
          className={
            stats.is_following
              ? "rounded-xl h-9 border-[#D4AF37] text-[#B8860B] dark:text-[#F5C76A] dark:border-[#F5C76A] dark:bg-transparent hover:bg-[#FAF0D7] dark:hover:bg-[#3A2E0E]"
              : "rounded-xl h-9 font-bold shadow-md shadow-amber-900/15 transition bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A] dark:from-[#F5C76A] dark:to-[#D4AF37] text-[#1A1A1A]"
          }
          data-testid="follow-button"
        >
          {stats.is_following
            ? (<><Check size={14} weight="bold" className="mr-1.5" /> Siguiendo</>)
            : (<><Plus size={14} weight="bold" className="mr-1.5" /> Seguir</>)
          }
        </Button>
        <Button
          onClick={() => setMsgOpen(true)}
          size="sm"
          variant="outline"
          className="rounded-xl h-9 border-gray-200 dark:border-white/15 dark:bg-transparent dark:text-white dark:hover:bg-white/5"
          data-testid="message-button"
        >
          <ChatCircle size={14} weight="duotone" className="mr-1.5" /> Mensaje
        </Button>
        {stats.followers_count > 0 && (
          <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-1" data-testid="followers-count">
            <b className="text-[#1A1A1A] dark:text-white">{stats.followers_count}</b> {stats.followers_count === 1 ? "seguidor" : "seguidores"}
          </span>
        )}
      </div>

      <MessageDialog open={msgOpen} onOpenChange={setMsgOpen} slug={slug} orgName={orgName} account={account} />
    </>
  );
}

function MessageDialog({ open, onOpenChange, slug, orgName, account }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (account && open) {
      setForm((f) => ({
        ...f,
        name: f.name || account.name || "",
        email: f.email || account.email || "",
        phone: f.phone || account.phone || "",
      }));
    }
  }, [account, open]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Completa nombre, email y un mensaje de al menos 5 caracteres");
      return;
    }
    setBusy(true);
    try {
      await customerApi.post(`/public/orgs/${slug}/messages`, form);
      toast.success("Mensaje enviado. El centro te responderá en breve.");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">Escribe a {orgName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-lg" data-testid="msg-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-lg" data-testid="msg-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-lg" data-testid="msg-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Mensaje *</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-lg" rows={5} maxLength={2000}
              placeholder="¿En qué podemos ayudarte? Indica modelo del coche y servicio que te interesa." data-testid="msg-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="rounded-lg font-bold bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A] text-[#1A1A1A]" data-testid="msg-submit">
            {busy ? "Enviando…" : (<><PaperPlaneTilt size={14} weight="fill" className="mr-1.5" /> Enviar</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
